<template>
    <BaseDialog :open="open" :title="`Feste Ordnung: ${selectedClass.name}, ${config.name}`" size="xl" :locked="open && dirty" @close="$emit('close')">
        <div class="editor">
            <div class="pool">
                <h3 class="leaf-title">Noch ohne Platz</h3>
                <div class="student-list" v-if="unseated.length > 0">
                    <button type="button" class="pill student" v-for="student in unseated" :key="student.id"
                        :aria-pressed="pickedId === student.id ? 'true' : 'false'" @click="pickStudent(student.id)">
                        {{ student.name }}
                    </button>
                </div>
                <p class="hint" v-else>Alle haben einen Platz.</p>
                <p class="form-note" v-if="tableCount < selectedClass.students.length">
                    {{ selectedClass.students.length - tableCount === 1 ? 'Es fehlt ein Tisch' : `Es fehlen ${selectedClass.students.length - tableCount} Tische` }}, damit alle einen Platz haben. Tische setzt du auf dem Sitzplan.
                </p>
                <div class="actions">
                    <button type="button" class="btn btn-sm" :disabled="currentCount === 0" title="Übernimmt die Plätze, die gerade auf dem Sitzplan stehen" @click="useCurrent">
                        <ClipboardCopy aria-hidden="true" /> Aktuellen Sitzplan übernehmen
                    </button>
                    <button type="button" class="btn btn-quiet btn-sm" :disabled="seatedCount === 0" @click="clearDraft">
                        <Eraser aria-hidden="true" /> Plätze leeren
                    </button>
                </div>
            </div>

            <div class="plan">
                <div class="status" role="status">
                    <p v-if="movingFrom !== null" class="form-note">
                        <span><strong>{{ names[draft[movingFrom]] }}</strong> ist ausgewählt. Klicke auf den neuen Platz, oder nochmals auf den Namen, um abzubrechen.</span>
                        <button type="button" class="btn btn-sm unseat" @click="unseat">Platz leeren</button>
                    </p>
                    <p v-else-if="pickedId" class="form-note"><span><strong>{{ names[pickedId] }}</strong> ist ausgewählt. Klicke auf einen Tisch.</span></p>
                    <p v-else class="status-hint">Klicke auf einen Namen in der Liste, dann auf einen Tisch. Einen gesetzten Namen verschiebst du, indem du ihn anklickst.</p>
                </div>
                <RoomPlan :config="config" :mirrored="isMirrored" v-slot="{ table }">
                    <Table
                        :studentName="names[draft[table.index]]"
                        :row="Math.floor(table.index / config.cols) + 1" :col="table.index % config.cols + 1"
                        :available="table.available"
                        :disabled="!table.available"
                        :moving="table.index === movingFrom"
                        v-on:toggle="clickTable(table)">
                    </Table>
                </RoomPlan>
                <div class="actions">
                    <button type="button" class="btn btn-quiet btn-sm" :aria-pressed="isMirrored ? 'true' : 'false'" title="Dreht die Ansicht um 180°: einmal von vorne, einmal von hinten gesehen" @click="isMirrored = !isMirrored">
                        <FlipVertical2 aria-hidden="true" /> Zimmer spiegeln
                    </button>
                </div>
            </div>
        </div>

        <div class="footer">
            <div class="actions">
                <button type="button" class="btn btn-primary" :disabled="seatedCount === 0 || !dirty" @click="$emit('save', { ...draft })">
                    <Save aria-hidden="true" /> Speichern
                </button>
                <button type="button" class="btn btn-quiet" @click="$emit('close')">Abbrechen</button>
                <span class="count">{{ seatedCount }} von {{ selectedClass.students.length }} haben einen Platz</span>
            </div>
            <div class="actions" v-if="hasSaved">
                <template v-if="confirmRemove">
                    <span class="confirm">Feste Ordnung löschen?</span>
                    <button type="button" class="btn btn-sm" @click="$emit('remove')">Löschen</button>
                    <button type="button" class="btn btn-quiet btn-sm" @click="confirmRemove = false">Abbrechen</button>
                </template>
                <button v-else type="button" class="btn btn-quiet btn-sm" @click="confirmRemove = true">
                    <Trash2 aria-hidden="true" /> Feste Ordnung löschen
                </button>
            </div>
        </div>
    </BaseDialog>
</template>

<script>
import { ClipboardCopy, Eraser, FlipVertical2, Save, Trash2 } from '@lucide/vue';
import BaseDialog from './ui/BaseDialog.vue';
import RoomPlan from './RoomPlan.vue';
import Table from './table.vue';

// Works on a draft: nothing changes until "Speichern".
export default {
    name: 'FixedPlanEditor',
    components: { BaseDialog, RoomPlan, Table, ClipboardCopy, Eraser, FlipVertical2, Save, Trash2 },
    props: {
        open: Boolean,
        config: Object,
        selectedClass: Object,
        // The saved arrangement, { tableIndex: studentId }
        saved: Object,
        // The plan on the board right now
        current: Object,
    },
    emits: ['save', 'remove', 'close'],
    data() {
        return {
            draft: {},
            // The student from the list who is about to get a seat
            pickedId: null,
            // The seat whose student is about to be moved
            movingFrom: null,
            isMirrored: false,
            confirmRemove: false,
        }
    },
    watch: {
        open: function(open) {
            if (!open) return
            this.draft = { ...this.saved }
            this.pickedId = null
            this.movingFrom = null
            this.confirmRemove = false
        },
    },
    computed: {
        names() {
            const names = {}
            this.selectedClass.students.forEach(s => { names[s.id] = s.name })
            return names
        },
        unseated() {
            const seated = Object.values(this.draft)
            return this.selectedClass.students.filter(s => !seated.includes(s.id))
        },
        seatedCount() {
            return Object.keys(this.draft).length
        },
        currentCount() {
            return Object.keys(this.current).length
        },
        tableCount() {
            return this.config.tables.filter(t => t.available).length
        },
        hasSaved() {
            return Object.keys(this.saved).length > 0
        },
        dirty() {
            const saved = this.saved
            const draft = this.draft
            return Object.keys(draft).length !== Object.keys(saved).length || Object.keys(draft).some(index => draft[index] !== saved[index])
        },
    },
    methods: {
        pickStudent: function(id) {
            this.pickedId = this.pickedId === id ? null : id
            this.movingFrom = null
        },
        // A student from the list takes the seat; whoever sat there goes back to the list.
        // A seated student is picked up and put down; whoever sat there takes the old seat.
        clickTable: function(table) {
            const draft = { ...this.draft }
            if (this.pickedId) {
                draft[table.index] = this.pickedId
                this.draft = draft
                this.pickedId = null
            } else if (this.movingFrom !== null) {
                if (table.index !== this.movingFrom) {
                    const moved = draft[this.movingFrom]
                    if (draft[table.index]) draft[this.movingFrom] = draft[table.index]
                    else delete draft[this.movingFrom]
                    draft[table.index] = moved
                    this.draft = draft
                }
                this.movingFrom = null
            } else if (draft[table.index]) {
                this.movingFrom = table.index
            }
        },
        unseat: function() {
            const draft = { ...this.draft }
            delete draft[this.movingFrom]
            this.draft = draft
            this.movingFrom = null
        },
        useCurrent: function() {
            this.draft = { ...this.current }
            this.pickedId = null
            this.movingFrom = null
        },
        clearDraft: function() {
            this.draft = {}
            this.pickedId = null
            this.movingFrom = null
        },
    },
}
</script>

<style scoped>
.editor {
    display: grid;
    grid-template-columns: minmax(240px, 1fr) minmax(0, 3fr);
    gap: var(--s-5);
    align-items: start;
}

.pool,
.plan {
    display: grid;
    gap: var(--s-3);
    min-width: 0;
}

.student-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-2);
}

.student {
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease);
}

.student[aria-pressed="false"]:hover {
    background: var(--hue-wash, var(--ground-wash));
}

.student[aria-pressed="true"] {
    background: var(--ink);
    color: var(--paper);
}

.status p {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--s-1) var(--s-4);
    min-height: 48px;
    padding-top: var(--s-1);
    padding-bottom: var(--s-1);
}

.status strong {
    font-weight: 800;
}

.status-hint {
    font-size: var(--t-sm);
    font-weight: 600;
    color: var(--ink-2);
}

.unseat {
    margin-left: auto;
}

.footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--s-3);
    padding-top: var(--s-4);
    border-top: var(--line);
}

.count,
.confirm {
    font-size: var(--t-sm);
    font-weight: 700;
}

@media (max-width: 899px) {
    .editor {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
